import { money } from "../api.js";
import { mountTourReplay, tourReplayButtonHtml } from "../tour.js";
import {
  actionCards,
  explainMoney,
  formatWhen,
  jobLabel,
  kpi,
  pct,
  ticketMixHtml,
  tip,
} from "../ux.js";

export function renderHome(pack) {
  const st = pack.stats || {};
  const roll = pack.product_rollup || {};
  const jobs = roll.job_type_counts || {};
  const brands = roll.brand_unit_counts || {};
  const n = pack.lead_count ?? st.leads_out ?? 0;
  const dealer = roll.dealer_name || roll.dealer_id || "";
  const where =
    pack.territory_id === "national"
      ? "Nationwide multi-market pack"
      : pack.territory_id || pack.metro || "Your territory";

  const jobLine = Object.entries(jobs)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${jobLabel(k)} (${v})`)
    .join(" · ");

  const brandLine = Object.entries(brands)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k} ×${v}`)
    .join(" · ");

  return `
    <h1>Your lead pipeline</h1>
    <p class="sub">
      ${where}
      ${dealer ? ` · <strong>${dealer}</strong>` : ""}
      ${pack.generated_at ? ` · Updated ${formatWhen(pack.generated_at)}` : ""}
    </p>

    ${tip(
      "What is LeadForge?",
      "A <strong>contractor-only</strong> playbook: ranked homes that fit outdoor living products, what to sell, and rough dollar potential. " +
        "It is not a CRM forecast and not permission to cold-call homeowners. Use Capture for legal opt-ins."
    )}

    <div class="kpis">
      ${kpi(n, "Leads ready", "Homes ranked for outreach / measure")}
      ${kpi(st.hot ?? "—", "Hot this week", "Start here first")}
      ${kpi(money(roll.avg_package_mid), "Typical ticket", "Middle of suggested package range")}
      ${kpi(pct(roll.avg_close_probability), "Avg. chance of close", "Planning estimate only")}
      ${kpi(money(roll.expected_pipeline_rev), "Likely revenue", "Sum of (ticket × chance) for all leads")}
      ${kpi(money(roll.expected_pipeline_conservative), "Safer case", "Likely revenue × 70%")}
    </div>

    ${explainMoney()}

    <h2>What should I do next?</h2>
    ${actionCards([
      {
        href: "#/pack",
        kicker: "1 · Sell",
        title: "Browse leads & packages",
        desc: "See each home, what to sell, and dollar range",
      },
      {
        href: "#/review",
        kicker: "2 · Verify",
        title: "Street View review",
        desc: "Mark measure-ready or reject before you drive",
      },
      {
        href: "#/ladder",
        kicker: "3 · Plan $",
        title: "Revenue & products",
        desc: "Ticket mix, brands, and top markets",
      },
      {
        href: "#/capture",
        kicker: "4 · Grow",
        title: "Homeowner form",
        desc: "Share this link for legal phone & email opt-ins",
      },
    ])}

    <div class="panel">
      <h2>This pack at a glance</h2>
      <p class="muted">${pack.offer || "Weekly outdoor living lead pack"} · for ${pack.buyer || "shade & patio dealers"}</p>
      <div class="stat-grid">
        <div>
          <div class="stat-label">Homes scored</div>
          <div class="stat-value">${st.homes_in ?? "—"}</div>
        </div>
        <div>
          <div class="stat-label">Leads kept</div>
          <div class="stat-value">${n}</div>
        </div>
        <div>
          <div class="stat-label">Package range (all leads)</div>
          <div class="stat-value small">${money(roll.sum_package_rev_low)} – ${money(roll.sum_package_rev_high)}</div>
        </div>
        <div>
          <div class="stat-label">Stretch case</div>
          <div class="stat-value small">${money(roll.expected_pipeline_stretch)}</div>
        </div>
      </div>
      <p style="margin-top:0.85rem"><strong>Job types:</strong> ${jobLine || "—"}</p>
      <p><strong>Brand units in suggestions:</strong> ${brandLine || "—"}</p>
      ${
        roll.dealer_floor_leads
          ? `<p class="muted">Dealer minimum ticket applied on <strong>${roll.dealer_floor_leads}</strong> leads (keeps packages in your $ band).</p>`
          : ""
      }
    </div>

    <div class="panel">
      <h2>Ticket sizes in this pack</h2>
      <p class="muted">How many homes fall into each installed-price band (planning).</p>
      ${ticketMixHtml(roll.ticket_buckets)}
    </div>

    <div class="panel">
      <h2>More tools</h2>
      <ul class="plain-list">
        <li><a href="#/pitch">Pitch kit</a> — email draft & top homes for a contractor conversation</li>
        <li><a href="#/3d">Site view</a> — map / schematic for a selected lead</li>
        <li><a href="#/leads">Inbound opt-ins</a> — people who filled out your Capture form</li>
      </ul>
      <div class="toolbar" style="margin-top:0.75rem">
        ${tourReplayButtonHtml()}
      </div>
    </div>

    <p class="muted small disclaimer-line">
      ${pack.disclaimer || "Contractor-facing intelligence only. No auto-contact of homeowners without consent."}
    </p>
  `;
}

export function mountHome() {
  mountTourReplay();
}
